import React, { useState, useEffect, useMemo } from 'react';
import { masterService } from '../../../services/masterService';

export const ObservationPickerDrawer = ({
  isOpen,
  onClose,
  alreadyMappedIds = [],
  onAddSelected,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFormat, setSelectedFormat] = useState('ALL');
  const [allObservations, setAllObservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Load observations when drawer opens
  useEffect(() => {
    if (!isOpen) return;

    let mounted = true;
    setLoading(true);
    setSelectedIds(new Set());
    setSearchTerm('');

    masterService.getObservations({ pageSize: 300, isActive: true })
      .then(res => {
        if (!mounted) return;
        const items = res?.data?.items || res?.data || [];
        setAllObservations(items);
      })
      .catch(err => {
        console.error('Failed to load observations', err);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [isOpen]);

  // Already mapped set for fast O(1) lookup
  const alreadyMappedSet = useMemo(() => {
    return new Set((alreadyMappedIds || []).map(id => String(id).trim().toUpperCase()));
  }, [alreadyMappedIds]);

  // Filtered available observations
  const filteredList = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return allObservations.filter(obs => {
      if (selectedFormat !== 'ALL' && obs.readingFormat !== selectedFormat) {
        return false;
      }
      if (!term) return true;
      const name = (obs.name || '').toLowerCase();
      const code = (obs.labObservationId || '').toLowerCase();
      const unit = (obs.unit || '').toLowerCase();
      return name.includes(term) || code.includes(term) || unit.includes(term);
    });
  }, [allObservations, searchTerm, selectedFormat]);

  // Available selectable count (excluding already mapped)
  const selectableCount = useMemo(() => {
    return filteredList.filter(o => !alreadyMappedSet.has(String(o.labObservationId).trim().toUpperCase())).length;
  }, [filteredList, alreadyMappedSet]);

  const handleToggle = (obs) => {
    const id = String(obs.labObservationId).trim().toUpperCase();
    if (alreadyMappedSet.has(id)) return; // prevent mapping duplicates

    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectAllAvailable = () => {
    const available = filteredList
      .filter(o => !alreadyMappedSet.has(String(o.labObservationId).trim().toUpperCase()))
      .map(o => String(o.labObservationId).trim().toUpperCase());

    setSelectedIds(new Set(available));
  };

  const handleClearSelection = () => {
    setSelectedIds(new Set());
  };

  const handleConfirmAdd = () => {
    const selectedObjs = allObservations.filter(o =>
      selectedIds.has(String(o.labObservationId).trim().toUpperCase())
    );
    onAddSelected(selectedObjs);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="iom-drawer-overlay" onClick={onClose}>
      <div className="iom-drawer-panel" onClick={e => e.stopPropagation()}>
        {/* Drawer Header */}
        <div className="iom-drawer-header">
          <div>
            <div className="iom-drawer-kicker">PARAMETER CATALOG</div>
            <h3 className="iom-drawer-title">Add Existing Parameters</h3>
            <p className="iom-drawer-sub">
              Search and multi-select parameters from the master analyte directory.
            </p>
          </div>
          <button type="button" className="iom-btn-close" onClick={onClose} title="Close drawer">
            ✕
          </button>
        </div>

        {/* Search & Filter Controls */}
        <div className="iom-drawer-filters">
          <div className="iom-search-box">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text"
              className="iom-input-search"
              placeholder="Search by analyte code, name, or unit..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              autoFocus
            />
            {searchTerm && (
              <button
                type="button"
                className="iom-search-clear-btn"
                onClick={() => setSearchTerm('')}
              >
                ✕
              </button>
            )}
          </div>

          <div className="iom-format-pills">
            {['ALL', 'Numeric', 'Text', 'Option', 'Formula'].map(fmt => (
              <button
                key={fmt}
                type="button"
                className={`iom-filter-pill ${selectedFormat === fmt ? 'active' : ''}`}
                onClick={() => setSelectedFormat(fmt)}
              >
                {fmt === 'ALL' ? 'All Formats' : fmt}
              </button>
            ))}
          </div>
        </div>

        {/* Action subbar */}
        <div className="iom-drawer-subbar">
          <div className="iom-subbar-count">
            Found <strong>{filteredList.length}</strong> parameters
            {selectedIds.size > 0 && (
              <span className="iom-badge-selected-count">
                {selectedIds.size} Selected
              </span>
            )}
          </div>
          <div className="iom-subbar-actions">
            {selectableCount > 0 && selectedIds.size < selectableCount && (
              <button
                type="button"
                className="iom-btn-link"
                onClick={handleSelectAllAvailable}
              >
                Select All Available ({selectableCount})
              </button>
            )}
            {selectedIds.size > 0 && (
              <button
                type="button"
                className="iom-btn-link text-muted"
                onClick={handleClearSelection}
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Parameters List Body */}
        <div className="iom-drawer-list">
          {loading ? (
            <div className="iom-loading-state">
              <div className="iom-spinner" />
              <span>Loading analyte master list...</span>
            </div>
          ) : filteredList.length === 0 ? (
            <div className="iom-empty-state">
              <div className="iom-empty-icon">🔍</div>
              <h4>No matching observations found</h4>
              <p>Try refining your search keyword or selecting "All Formats".</p>
            </div>
          ) : (
            <div className="iom-obs-items">
              {filteredList.map(obs => {
                const id = String(obs.labObservationId).trim().toUpperCase();
                const isAlreadyMapped = alreadyMappedSet.has(id);
                const isSelected = selectedIds.has(id);

                return (
                  <div
                    key={obs.id || obs.labObservationId}
                    className={`iom-obs-card ${isSelected ? 'selected' : ''} ${isAlreadyMapped ? 'disabled' : ''}`}
                    onClick={() => !isAlreadyMapped && handleToggle(obs)}
                  >
                    <div className="iom-obs-card-select">
                      <input
                        type="checkbox"
                        checked={isSelected || isAlreadyMapped}
                        disabled={isAlreadyMapped}
                        onChange={() => handleToggle(obs)}
                        onClick={e => e.stopPropagation()}
                        aria-label={`Select ${obs.name}`}
                      />
                    </div>

                    <div className="iom-obs-card-body">
                      <div className="iom-obs-title-row">
                        <span className="iom-obs-name">{obs.name}</span>
                        {isAlreadyMapped && (
                          <span className="iom-badge-already-mapped" title="Parameter is already mapped to this investigation">
                            ✓ Already Mapped
                          </span>
                        )}
                      </div>

                      <div className="iom-obs-meta-row">
                        <span className="iom-meta-code">{obs.labObservationId}</span>
                        <span className="iom-meta-dot">•</span>
                        <span className="iom-meta-format">{obs.readingFormat || 'Numeric'}</span>
                        {obs.unit && (
                          <>
                            <span className="iom-meta-dot">•</span>
                            <span className="iom-meta-unit">{obs.unit}</span>
                          </>
                        )}
                        {obs.roundUpto > 0 && (
                          <>
                            <span className="iom-meta-dot">•</span>
                            <span className="iom-meta-prec">{obs.roundUpto} decimals</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Drawer Footer Actions */}
        <div className="iom-drawer-footer">
          <div className="iom-footer-summary">
            {selectedIds.size === 0 ? (
              <span className="text-muted">No parameters selected</span>
            ) : (
              <span>
                <strong>{selectedIds.size}</strong> parameter{selectedIds.size > 1 ? 's' : ''} ready to map
              </span>
            )}
          </div>
          <div className="iom-footer-buttons">
            <button
              type="button"
              className="iom-btn iom-btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="iom-btn iom-btn-primary"
              disabled={selectedIds.size === 0}
              onClick={handleConfirmAdd}
            >
              Add Selected ({selectedIds.size})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
